import { t } from '@lingui/core/macro';
import { brands } from 'noddi-types';
import { cn } from 'noddi-ui-common';
import { NoddiLanguageSelector, NoddiSidebar, SidebarInset, SidebarProvider } from 'noddi-ui-web';
import { customerRoutes } from 'noddi-util';
import { ImpersonateMenuItem, tracking, useAuthContext, useLanguageContext } from 'noddi-web';
import { Outlet, useLocation } from 'react-router-dom';
import { useNavItems } from '../../hooks/useNavItems';
import { useOnImpersonated } from '../../hooks/useOnImpersonated';
import { useCurrentBrand } from '../../stores/BrandStore/brandStore';
import { getSupportedLocales } from '../../utils/translation';
import { NavioContactChatWidget } from '../BookingFlow/Common/NavioContactChatWidget';
import { BrandLogo } from '../Brand/BrandLogo';
import { CustomerNoticeTopBanner } from '../Common/CustomerNoticeTopBanner';
import { NoticeBanner } from '../Common/NoticeBanner';
import { HomeHeaderNav } from './HomeHeaderNav';
import { PageLayoutProvider } from './PageLayoutProvider';
import { SkipContentTarget, SkipToMainContent } from './SkipToMainContent';
import { TronderdekkHeaderNav } from './TronderdekkHeaderNav';
import { usePageBackground } from './usePageBackground';
import { usePageWidth } from './usePageWidth';

const SidebarNav = () => {
  const { currentBrand } = useCurrentBrand();
  const width = usePageWidth();

  if (currentBrand === brands.tronderdekk) {
    return <TronderdekkHeaderNav />;
  }
  return <HomeHeaderNav width={width} />;
};

const SidebarLayoutInner = () => {
  const { currentBrand } = useCurrentBrand();

  const { logout, userData } = useAuthContext();
  const navItems = useNavItems(currentBrand);
  const { pathname } = useLocation();
  const { currentLanguage, setCurrentLanguage } = useLanguageContext();
  const onImpersonated = useOnImpersonated();

  const pageWidth = usePageWidth();
  const isWidePage = pageWidth === 'wide';
  const background = usePageBackground();

  const hideChat =
    pathname.includes(customerRoutes.homepage.getBasePath()) ||
    pathname.includes(customerRoutes.bookings.getBasePath()) ||
    // The full path: the base `/fleet` would also match the department calendar at `/fleet/:slug/calendar`.
    pathname.includes(customerRoutes.fleet.getPath()) ||
    pathname.includes('confirmation/');

  const filteredNavItems = navItems.filter((item) => {
    return item.items[0]?.hide !== true;
  });

  return (
    <SidebarProvider onlyMobile>
      <NoddiSidebar
        navItems={filteredNavItems}
        // oxlint-disable-next-line typescript/no-misused-promises -- async onLogout runs logout(); React discards the returned promise, so a rejection is unhandled unless the body catches
        onLogout={logout}
        profilePath={customerRoutes.profile.getPath()}
        profileLabel={t`My account`}
        logoutLabel={t`Log out`}
        user={userData?.user}
        header={
          <div className='flex items-center justify-center p-4'>
            <BrandLogo />
          </div>
        }
        extraMenuItems={<ImpersonateMenuItem onImpersonated={onImpersonated} />}
        footer={
          <NoddiLanguageSelector
            className='bg-transparent text-sidebar-foreground'
            supportedLanguages={getSupportedLocales()}
            currentLanguage={currentLanguage}
            setCurrentLanguage={(locale) => {
              tracking.track('languageChanged', { language: locale });
              setCurrentLanguage(locale);
            }}
          />
        }
      />
      <SidebarInset>
        <div
          className={cn(
            'min-h-screen',
            background === 'grouped' && 'bg-grouped-background',
            !background && (isWidePage ? 'bg-tertiary-background' : 'bg-primary-background-gradient')
          )}
        >
          <SkipToMainContent />
          <CustomerNoticeTopBanner />
          <SidebarNav />
          <SkipContentTarget>
            <NoticeBanner />
            <Outlet />
          </SkipContentTarget>
          {!hideChat && (
            <div className='fixed right-10 bottom-10 z-docked'>
              <NavioContactChatWidget size='small' />
            </div>
          )}
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
};

export const SidebarLayout = () => {
  return (
    <PageLayoutProvider>
      <SidebarLayoutInner />
    </PageLayoutProvider>
  );
};
